// React and router imports / Importaciones de React y router
import { NavLink } from 'react-router-dom';
import { useEffect, useRef, useState, memo, useCallback, useMemo } from 'react';

// Icons / Iconos
import { FaShoppingCart, FaTimes, FaUser, FaSignInAlt, FaSignOutAlt, FaClipboardList } from 'react-icons/fa';

// Context and components / Contexto y componentes
import { useCart } from '../context/CartContext';
import { useAuth } from '../features/auth/context/AuthContext';
import Logo from './common/Logo';

// Styles / Estilos
import '../styles/navbar.css';
import '../styles/vv-colors.css';

// Navbar component - Handles navigation and cart access
// Componente Navbar - Maneja la navegación y acceso al carrito
const Navbar = ({ onCartClick }) => {
  const { totalItems } = useCart();
  const { user, isAuthenticated, logout, userRole } = useAuth();
  const navbarCollapseRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  // Function to close the mobile menu / Función para cerrar el menú móvil
  const closeNavbar = useCallback(() => {
    if (window.innerWidth < 992) { // Only on mobile/tablet
      const bsCollapse = navbarCollapseRef.current;
      if (bsCollapse && window.bootstrap) {
        const collapse = new window.bootstrap.Collapse(bsCollapse, { toggle: false });
        collapse.hide();
        setIsMenuOpen(false);
      }
    }
  }, []);
  
  // Function to open the mobile menu immediately / Función para abrir el menú móvil inmediatamente
  const openNavbar = useCallback(() => {
    if (window.innerWidth < 992) { // Only on mobile/tablet
      setIsMenuOpen(true); // Set state immediately
    }
  }, []);
  
  // Handle cart click from mobile menu
  // Manejar clic en el carrito desde el menú móvil
  const handleMobileCartClick = useCallback(() => {
    closeNavbar();
    onCartClick();
  }, [closeNavbar, onCartClick]);
  
  // Handle logout
  // Manejar cierre de sesión
  const handleLogout = useCallback(() => {
    closeNavbar();
    logout();
  }, [closeNavbar, logout]);

  // Listen for Bootstrap collapse events to update state and ensure menu is closed on load
  // Escuchar eventos de colapso de Bootstrap para actualizar el estado y asegurar que el menú esté cerrado al cargar
  useEffect(() => {
    const handleNavbarShown = () => setIsMenuOpen(true);
    const handleNavbarHidden = () => setIsMenuOpen(false);
    
    const navbarElement = navbarCollapseRef.current;
    if (navbarElement) {
      // Ensure menu is closed on initial load
      if (navbarElement.classList.contains('show')) {
        navbarElement.classList.remove('show');
        navbarElement.classList.add('collapse');
        setIsMenuOpen(false);
        document.body.style.overflow = '';
      }
      
      navbarElement.addEventListener('shown.bs.collapse', handleNavbarShown);
      navbarElement.addEventListener('hidden.bs.collapse', handleNavbarHidden);
      
      // Force body to be non-scrollable when menu is open
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.target.classList.contains('show')) {
            document.body.classList.add('menu-open');
          } else {
            document.body.classList.remove('menu-open');
          }
        });
      });
      
      observer.observe(navbarElement, { attributes: true, attributeFilter: ['class'] });
      
      return () => {
        navbarElement.removeEventListener('shown.bs.collapse', handleNavbarShown);
        navbarElement.removeEventListener('hidden.bs.collapse', handleNavbarHidden);
        observer.disconnect();
        document.body.classList.remove('menu-open');
      };
    }
  }, []);
  
  // Memoize the navbar content to prevent unnecessary re-renders
  // Memorizar el contenido de la barra de navegación para evitar re-renderizados innecesarios
  const navbarContent = useMemo(() => (
    <nav className="navbar navbar-expand-lg navbar-light bg-white sticky-top shadow-sm">
      <div className="container position-relative">
        <NavLink to="/" className="navbar-brand">
          <Logo size="medium" showText={false} />
        </NavLink>
        
        {/* Icono del carrito - Visible en todos los tamaños de pantalla */}
        <div className="d-flex ms-auto me-2 me-lg-0 order-lg-last">
          <button 
            className="btn btn-outline-secondary rounded-circle cart-button border-vv-yellow"
            onClick={onCartClick}
            aria-label="Abrir carrito"
          >
            <FaShoppingCart />
            <span className={`position-absolute top-0 start-100 translate-middle badge rounded-pill bg-vv-yellow cart-badge ${totalItems > 0 ? 'visible' : ''}`}>
              {totalItems}
              <span className="visually-hidden">productos en el carrito</span>
            </span>
          </button>
        </div>
        
        <button
          className="navbar-toggler border-vv-yellow"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarNav"
          aria-controls="navbarNav"
          aria-expanded="false"
          aria-label="Toggle navigation"
          onClick={openNavbar}
        >
          <span className="navbar-toggler-icon"></span>
        </button>
        
        <div className="collapse navbar-collapse" id="navbarNav" ref={navbarCollapseRef}>
          {/* Botón de cierre para menú móvil - solo visible en móvil cuando el menú está abierto */}
          <button 
            className={`btn-close navbar-close-btn d-lg-none ${isMenuOpen ? 'visible' : ''}`}
            onClick={closeNavbar} 
            aria-label="Close menu"
          ></button>
          
          <ul className="navbar-nav ms-auto">
            <li className="nav-item">
              <NavLink 
                className={({ isActive }) => 
                  `nav-link nunito-sans ${isActive ? 'active fw-bold text-vv-yellow' : ''} px-3`
                } 
                to="/"
                onClick={closeNavbar}
              >
                Inicio
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink 
                className={({ isActive }) => {
                  // Obtener la ruta actual
                  const pathname = window.location.pathname;
                  // Considerar activo si estamos en el catálogo o en cualquier ruta relacionada
                  const isCatalogActive = isActive || 
                    pathname.startsWith('/category/') || 
                    pathname.startsWith('/product/') || 
                    pathname.startsWith('/medical-products');
                  return `nav-link nunito-sans ${isCatalogActive ? 'active fw-bold text-vv-yellow' : ''} px-3`;
                }} 
                to="/catalog"
                onClick={closeNavbar}
              >
                Catálogo
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink 
                className={({ isActive }) => 
                  `nav-link nunito-sans ${isActive ? 'active fw-bold text-vv-yellow' : ''} px-3`
                } 
                to="/about-test"
                onClick={closeNavbar}
              >
                Sobre nosotros
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink 
                className={({ isActive }) => 
                  `nav-link nunito-sans ${isActive ? 'active fw-bold text-vv-yellow' : ''} px-3`
                } 
                to="/contact"
                onClick={closeNavbar}
              >
                Contáctanos
              </NavLink>
            </li>
            
            {/* Authentication Links */}
            {isAuthenticated ? (
              <>
                {/* Show user menu when logged in */}
                <li className="nav-item dropdown">
                  <a 
                    className="nav-link dropdown-toggle d-flex align-items-center px-3" 
                    href="#" 
                    id="navbarDropdown" 
                    role="button" 
                    data-bs-toggle="dropdown" 
                    aria-expanded="false"
                  >
                    <FaUser className="me-1" />
                    <span className="d-none d-lg-inline">
                      {user ? (user.get('name') || user.get('email')) : 'Mi Cuenta'}
                    </span>
                  </a>
                  <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="navbarDropdown">
                    <li>
                      <NavLink 
                        className="dropdown-item d-flex align-items-center" 
                        to="/user/dashboard"
                        onClick={closeNavbar}
                      >
                        <FaUser className="me-2" />
                        Mi Dashboard
                      </NavLink>
                    </li>
                    <li>
                      <NavLink 
                        className="dropdown-item d-flex align-items-center" 
                        to="/user/profile"
                        onClick={closeNavbar}
                      >
                        <FaUser className="me-2" />
                        Mi Perfil
                      </NavLink>
                    </li>
                    <li>
                      <NavLink 
                        className="dropdown-item d-flex align-items-center" 
                        to="/user/quotations"
                        onClick={closeNavbar}
                      >
                        <FaClipboardList className="me-2" />
                        Mis Cotizaciones
                      </NavLink>
                    </li>
                    {userRole === 'admin' && (
                      <li>
                        <NavLink 
                          className="dropdown-item d-flex align-items-center" 
                          to="/admin"
                          onClick={closeNavbar}
                        >
                          <FaUser className="me-2" />
                          Admin Panel
                        </NavLink>
                      </li>
                    )}
                    <li><hr className="dropdown-divider" /></li>
                    <li>
                      <button 
                        className="dropdown-item d-flex align-items-center text-danger" 
                        onClick={handleLogout}
                      >
                        <FaSignOutAlt className="me-2" />
                        Cerrar Sesión
                      </button>
                    </li>
                  </ul>
                </li>
              </>
            ) : (
              <>
                {/* Show login/register when logged out */}
                <li className="nav-item">
                  <NavLink 
                    className={({ isActive }) => 
                      `nav-link nunito-sans d-flex align-items-center ${isActive ? 'active fw-bold text-vv-yellow' : ''} px-3`
                    } 
                    to="/login"
                    onClick={closeNavbar}
                  >
                    <FaSignInAlt className="me-1" />
                    <span>Iniciar Sesión</span>
                  </NavLink>
                </li>
              </>
            )}
          </ul>
          
          {/* Botón del carrito en el menú móvil para acceso rápido */}
          <button 
            className="mobile-menu-cart d-lg-none text-vv-yellow"
            onClick={handleMobileCartClick}
            aria-label="Ver carrito"
          >
            <FaShoppingCart className="me-2" />
            Ver carrito {totalItems > 0 ? `(${totalItems})` : ''}
          </button>
        </div>
      </div>
    </nav>
  ), [totalItems, onCartClick, isMenuOpen, closeNavbar, openNavbar, handleMobileCartClick, isAuthenticated, user, userRole, handleLogout]);
  
  return navbarContent;
};

// Memoize the component to prevent unnecessary re-renders
// Memorizar el componente para evitar renderizados innecesarios
export default memo(Navbar);
